<template>
  <div class="vendor_add-container glass-tab-page">
    <div class="vendor_add-header">
      <BrucdcumHeader :breadcrumb="['Vendor', 'New Vendor']" />
    </div>
    <form class="vendor_add-form" @submit.prevent="handleNext">
      <div class="glass-tab-view">
      <div class="glass-tab-view__tabs">
        <TabNavigation
          v-model="activeTab"
          :tabs="tabs"
          @update:modelValue="handleTabChange"
        />
      </div>

      <div class="glass-tab-view__content">
        <div class="glass-tab-view__panel" v-if="activeTab === 'general'">
        <div class="vendor_add-form-section">
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-id"
                label="Vendor ID"
                v-model="vendorId"
                placeholder="Enter Vendor ID"
                required
                flex
                :has-error="vendorIdExists || (showErrors && !vendorId)"
                @input="handleVendorIdInput"
              >
                <div v-if="checkingVendorId" class="vendor_add-checking-text">Checking availability...</div>
                <div v-if="vendorIdExists" class="vendor_add-error-text">This Vendor ID already exists in this company.</div>
                <div v-if="showErrors && !vendorId" class="vendor_add-error-text">Vendor ID is required.</div>
              </LabelInputField>
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-name"
                label="Vendor Name"
                v-model="vendorName"
                placeholder="Enter Vendor Name"
                required
                flex
                :has-error="showErrors && !vendorName"
                @input="clearVendorNameError"
              >
                <div v-if="showErrors && !vendorName" class="vendor_add-error-text">Vendor Name is required.</div>
              </LabelInputField>
            </div>
          </div>
          <div class="vendor_add-form-group-row vendor_add-form-group-row--field-pair">
            <div class="vendor_add-form-group vendor_add-form-group--label-checkbox">
              <LabelCheckboxField
                input-id="new-vendor-active"
                label="Active"
                v-model="active"
                flex
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Vendor Type"
                select-id="new-vendor-role"
                v-model="vendorRole"
                wide
                :searchable="true"
                :options="vendorRoleDropdownOptions"
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-date">
              <LabelDateField
                input-id="new-vendor-vendor-since"
                label="Vendor Since"
                v-model="vendorSince"
                flex
                @change="handleVendorSinceChange"
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-opening-balance"
                label="Opening Balance (USD)"
                v-model="openingBalance"
                type="number"
                flex
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-date">
              <LabelDateField
                input-id="new-vendor-balance-as-of"
                label="Balance as of"
                v-model="balanceAsOf"
                flex
                @change="handleBalanceAsOfChange"
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-balance"
                label="Balance (USD)"
                v-model="balance"
                type="number"
                flex
                disabled
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-fax"
                label="Fax"
                v-model="fax"
                flex
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-webpage"
                label="Web page address"
                v-model="webpage"
                flex
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-tax-id"
                label="Tax ID"
                v-model="taxId"
                flex
              />
            </div>
          </div>
        </div>
        <div class="vendor_add-form-group vendor_add-full-width vendor_add-form-group--label-description">
          <LabelDescriptionField
            input-id="new-vendor-notes"
            label="Notes"
            v-model="notes"
            :rows="3"
            flex
          />
        </div>
        </div>

        <div class="glass-tab-view__panel" v-else-if="activeTab === 'contacts'">
        <div class="vendor_add-contacts-section">
          <div class="vendor_add-contacts-header">
            <h3>Contacts</h3>
            <div class="vendor_add-add-contact-container">
              <PrimaryButton
                label="Add Contact"
                icon="bi bi-plus-lg"
                @click="addContactRow"
              />
              
              <!-- Tooltip for when no contacts exist -->
              <div v-if="contacts.length === 0 && newContacts.length === 0" class="vendor_add-add-contact-tooltip">
                <div class="vendor_add-tooltip-content">
                  <div class="vendor_add-tooltip-icon">👥</div>
                  <p>No contacts added yet</p>
                  <p class="vendor_add-tooltip-subtitle">Add contacts for this vendor</p>
                </div>
              </div>
            </div>
          </div>
          
          
          <!-- Contact Cards -->
          <div class="vendor_add-contacts-grid">
            <!-- Existing Contacts -->
            <div v-for="(contact, idx) in contacts" :key="'contact-' + idx" class="vendor_add-contact-card">
              <div class="vendor_add-contact-card-header">
                <LabelCheckboxField
                  :input-id="`vendor-contact-primary-${idx}`"
                  :label="contacts.length === 1 ? 'Primary Contact *' : 'Primary Contact'"
                  v-model="contact.primary"
                  custom-class="label-checkbox-field--card-compact"
                  :disabled="contacts.length === 1 || (contact.primary && getNumberOfPrimaryContacts() === 1)"
                  @change="() => setPrimaryContact(idx)"
                />
              </div>
              <div class="vendor_add-contact-fields">
                <div class="vendor_add-contact-field-row">
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-contact-name-${idx}`"
                      label="Contact Name"
                      v-model="contact.name"
                      flex
                      :readonly="editingContactIdx !== idx"
                    />
                  </div>
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-contact-title-${idx}`"
                      label="Title"
                      v-model="contact.title"
                      flex
                      :readonly="editingContactIdx !== idx"
                    />
                  </div>
                </div>
                <div class="vendor_add-contact-field-row">
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-contact-phone-${idx}`"
                      label="Business Phone"
                      v-model="contact.phone"
                      flex
                      :readonly="editingContactIdx !== idx"
                    />
                  </div>
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-contact-email-${idx}`"
                      label="Email"
                      v-model="contact.email"
                      type="email"
                      flex
                      :readonly="editingContactIdx !== idx"
                      :has-error="!!contactEmailErrors[`contact-${idx}`]"
                      @blur="validateContactEmail(contact.email, idx, false)"
                      @input="handleContactEmailInput(contact.email, idx, false)"
                    >
                      <div v-if="checkingContactEmails[`contact-${idx}`]" class="vendor_add-checking-text">Checking availability...</div>
                      <div v-if="contactEmailErrors[`contact-${idx}`]" class="vendor_add-error-text">{{ contactEmailErrors[`contact-${idx}`] }}</div>
                    </LabelInputField>
                  </div>
                </div>
              </div>
              <div v-if="editingContactIdx !== idx" class="vendor_add-contact-save-actions">
                <SecondaryButton label="Edit" icon="bi bi-pencil" @click.prevent="editContact(idx)" />
                <SecondaryButton
                  label="Delete"
                  icon="bi bi-trash"
                  custom-class="consultant-secondary-btn--danger"
                  @click.prevent="deleteContact(idx)"
                />
              </div>
              <div v-if="editingContactIdx === idx" class="vendor_add-contact-save-actions">
                <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelEditContact" />
                <PrimaryButton label="Update" icon="bi bi-check-lg" @click.prevent="updateContact()" />
              </div>
            </div>

            <!-- New Contact Cards -->
            <div v-for="(nc, nidx) in newContacts" :key="'new-contact-' + nidx" class="vendor_add-contact-card vendor_add-contact-card-new">
              <div class="vendor_add-contact-card-header">
                <LabelCheckboxField
                  :input-id="`vendor-new-contact-primary-${nidx}`"
                  :label="contacts.length + newContacts.length === 1 ? 'Primary Contact *' : 'Primary Contact'"
                  v-model="nc.primary"
                  custom-class="label-checkbox-field--card-compact"
                  :disabled="(contacts.length + newContacts.length === 1) || (nc.primary && getNumberOfPrimaryContacts() === 1)"
                  @change="setPrimaryContactNew(nidx)"
                />
              </div>
              <div class="vendor_add-contact-fields">
                <div class="vendor_add-contact-field-row">
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-new-contact-name-${nidx}`"
                      label="Contact Name"
                      v-model="nc.name"
                      placeholder="Contact Name"
                      flex
                    />
                  </div>
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-new-contact-title-${nidx}`"
                      label="Title"
                      v-model="nc.title"
                      placeholder="Title"
                      flex
                    />
                  </div>
                </div>
                <div class="vendor_add-contact-field-row">
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-new-contact-phone-${nidx}`"
                      label="Business Phone"
                      v-model="nc.phone"
                      placeholder="Business Phone"
                      flex
                    />
                  </div>
                  <div class="vendor_add-contact-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-new-contact-email-${nidx}`"
                      label="Email"
                      v-model="nc.email"
                      type="email"
                      placeholder="Email"
                      flex
                      :has-error="!!contactEmailErrors[`new-contact-${nidx}`]"
                      @blur="validateContactEmail(nc.email, nidx, true)"
                      @input="handleContactEmailInput(nc.email, nidx, true)"
                    >
                      <div v-if="checkingContactEmails[`new-contact-${nidx}`]" class="vendor_add-checking-text">Checking availability...</div>
                      <div v-if="contactEmailErrors[`new-contact-${nidx}`]" class="vendor_add-error-text">{{ contactEmailErrors[`new-contact-${nidx}`] }}</div>
                    </LabelInputField>
                  </div>
                </div>
              </div>
              <div class="vendor_add-contact-save-actions">
                <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelContact(nidx)" />
                <PrimaryButton label="Save" icon="bi bi-check-lg" @click.prevent="saveContact(nidx)" />
              </div>
            </div>
          </div>
        </div>
        </div>

        <div class="glass-tab-view__panel" v-else-if="activeTab === 'addresses'">
        <div class="vendor_add-contacts-section">
          <div class="vendor_add-addresses-header">
            <h3>Addresses</h3>
            <div class="vendor_add-add-address-container">
              <PrimaryButton
                label="Add Address"
                icon="bi bi-plus-lg"
                @click="addNewAddressRow"
              />
              
              <!-- Tooltip for when no addresses exist -->
              <div v-if="addresses.length === 0 && newAddresses.length === 0" class="vendor_add-add-address-tooltip">
                <div class="vendor_add-tooltip-content">
                  <div class="vendor_add-tooltip-icon">📍</div>
                  <p>No addresses added yet</p>
                  <p class="vendor_add-tooltip-subtitle">Add addresses for this vendor</p>
                </div>
              </div>
            </div>
          </div>
          
          
          <!-- Address Cards -->
          <div class="vendor_add-addresses-grid">
            <!-- Existing Addresses -->
            <div v-for="(address, idx) in addresses" :key="'address-' + idx" class="vendor_add-address-card">
              <div class="vendor_add-address-card-header vendor_add-form-group--label-dropdown">
                <LabelDropdown
                  label="Address Type"
                  :select-id="`vendor-address-type-${idx}`"
                  v-model="address.type"
                  wide
                  required
                  :disabled="editingAddressIdx !== idx"
                  :options="addressTypeDropdownOptions(idx, false)"
                />
              </div>
              <div class="vendor_add-address-fields">
                <div class="vendor_add-address-field-row">
                  <div class="vendor_add-address-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-address-line1-${idx}`"
                      label="Address Line 1"
                      v-model="address.address1"
                      placeholder="Street address"
                      flex
                      :readonly="editingAddressIdx !== idx"
                    />
                  </div>
                  <div class="vendor_add-address-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-address-line2-${idx}`"
                      label="Address Line 2"
                      v-model="address.address2"
                      placeholder="Apartment, suite, etc."
                      flex
                      :readonly="editingAddressIdx !== idx"
                    />
                  </div>
                </div>
                <AddressSelector
                  :country="address.country"
                  :state="address.state"
                  :city="address.city"
                  :postal-code="address.postalCode"
                  :disabled="editingAddressIdx !== idx"
                  @update:country="address.country = $event"
                  @update:state="address.state = $event"
                  @update:city="address.city = $event"
                  @update:postalCode="address.postalCode = $event"
                />
              </div>
              <div v-if="editingAddressIdx !== idx" class="vendor_add-address-save-actions">
                <SecondaryButton label="Edit" icon="bi bi-pencil" @click.prevent="editAddress(idx)" />
                <SecondaryButton
                  label="Delete"
                  icon="bi bi-trash"
                  custom-class="consultant-secondary-btn--danger"
                  @click.prevent="deleteAddress(idx)"
                />
              </div>
              <div v-if="editingAddressIdx === idx" class="vendor_add-address-save-actions">
                <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelEditAddress" />
                <PrimaryButton label="Update" icon="bi bi-check-lg" @click.prevent="updateAddress(idx)" />
              </div>
            </div>

            <!-- New Address Cards -->
            <div v-for="(na, nidx) in newAddresses" :key="'new-address-' + nidx" class="vendor_add-address-card vendor_add-address-card-new">
              <div class="vendor_add-address-card-header vendor_add-form-group--label-dropdown">
                <LabelDropdown
                  label="Address Type"
                  :select-id="`vendor-new-address-type-${nidx}`"
                  v-model="na.type"
                  wide
                  required
                  :options="addressTypeDropdownOptions(nidx, true)"
                />
              </div>
              <div class="vendor_add-address-fields">
                <div class="vendor_add-address-field-row">
                  <div class="vendor_add-address-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-new-address-line1-${nidx}`"
                      label="Address Line 1"
                      v-model="na.address1"
                      placeholder="Street address"
                      flex
                    />
                  </div>
                  <div class="vendor_add-address-field vendor_add-form-group--label-input">
                    <LabelInputField
                      :input-id="`vendor-new-address-line2-${nidx}`"
                      label="Address Line 2"
                      v-model="na.address2"
                      placeholder="Apartment, suite, etc."
                      flex
                    />
                  </div>
                </div>
                <AddressSelector
                  :country="na.country"
                  :state="na.state"
                  :city="na.city"
                  :postal-code="na.postalCode"
                  @update:country="na.country = $event"
                  @update:state="na.state = $event"
                  @update:city="na.city = $event"
                  @update:postalCode="na.postalCode = $event"
                />
              </div>
              <div class="vendor_add-address-save-actions">
                <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelNewAddress(nidx)" />
                <PrimaryButton label="Save" icon="bi bi-check-lg" @click.prevent="saveNewAddress(nidx)" />
              </div>
            </div>
          </div>
        </div>
        </div>

        <div class="glass-tab-view__panel" v-else-if="activeTab === 'details'">
        <div class="vendor_add-form-section">
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Payment Terms"
                select-id="new-vendor-payment-terms"
                v-model="paymentTermsModel"
                wide
                required
                :searchable="true"
                :options="paymentTermsDropdownOptions"
                :has-error="showErrors && !paymentTerms"
                @change="handlePaymentTermChangeAndClearError"
              />
              <div v-if="showErrors && !paymentTerms" class="vendor_add-error-text">Payment Terms is required.</div>
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Payment Method"
                select-id="new-vendor-payment-method"
                v-model="paymentMethod"
                wide
                required
                :options="paymentMethodDropdownOptions"
                :has-error="showErrors && !paymentMethod"
                @change="clearPaymentMethodError"
              />
              <div v-if="showErrors && !paymentMethod" class="vendor_add-error-text">Payment Method is required.</div>
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-bank-name"
                label="Bank Name"
                v-model="bankName"
                placeholder="Bank Name"
                flex
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Bank Account Type"
                select-id="new-vendor-bank-account-type"
                v-model="bankAccountType"
                wide
                :options="bankAccountTypeDropdownOptions"
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-bank-routing"
                label="Bank Routing Number"
                v-model="bankRoutingNumber"
                placeholder="Bank Routing Number"
                flex
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-account-number"
                label="Account Number"
                v-model="accountNumber"
                placeholder="Account Number"
                flex
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-bank-branch"
                label="Bank Branch"
                v-model="bankBranch"
                placeholder="Bank Branch"
                flex
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-credit-limit"
                label="Credit Limit(USD)"
                v-model="creditLimit"
                type="number"
                flex
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Account"
                select-id="new-vendor-account"
                v-model="accountModel"
                wide
                :searchable="true"
                :options="accountDropdownOptions"
                @change="handleAccountChange"
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Preferred shipping method"
                select-id="new-vendor-shipping-method"
                v-model="shippingMethodModel"
                wide
                :searchable="true"
                :options="shippingMethodDropdownOptions"
                @change="handleShippingMethodChange"
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Vendor Group"
                select-id="new-vendor-vendor-group"
                v-model="vendorGroupModel"
                wide
                :searchable="true"
                :options="vendorGroupDropdownOptions"
                @change="handleVendorGroupChange"
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-tax-registration"
                label="TAX Registration Number"
                v-model="taxRegistrationNumber"
                placeholder="TAX Registration Number"
                flex
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-form-group--label-dropdown">
              <LabelDropdown
                label="Tax Code"
                select-id="new-vendor-tax-code"
                v-model="taxCode"
                wide
                :options="taxCodeDropdownOptions"
              />
            </div>
            <div class="vendor_add-form-group vendor_add-form-group--label-input">
              <LabelInputField
                input-id="new-vendor-service-tax"
                label="Service tax registration no"
                v-model="serviceTaxRegistrationNo"
                placeholder="Service tax registration no"
                flex
              />
            </div>
          </div>
          <div class="vendor_add-form-group-row">
            <div class="vendor_add-form-group vendor_add-full-width vendor_add-custom-fields-row">
              <span class="vendor_add-custom-fields-label">Custom Fields</span>
              <SecondaryButton
                label="Manage Custom Fields"
                icon="bi bi-sliders"
                @click="showCustomFieldsModal = true"
              />
            </div>
          </div>
        </div>
        </div>

        <div class="glass-tab-view__panel" v-else-if="activeTab === 'documents'">
        <div class="vendor_add-documents-section">
          <div class="vendor_add-documents-header">
            <h3>Documents</h3>
            <div class="vendor_add-add-address-container">
              <PrimaryButton
                label="Add Document"
                icon="bi bi-plus-lg"
                @click="$refs.addDocumentFileInput.click()"
              />
              <input
                id="add-vendor-document-input"
                type="file"
                ref="addDocumentFileInput"
                class="vendor_add-document-file-input"
                @change="handleDocumentFileUpload"
                multiple
              />
            </div>
          </div>
          
          <!-- Split View: Documents List (Left) and Preview (Right) -->
          <div class="vendor_add-documents-split-view">
            <!-- Left Side: Documents List -->
            <div class="vendor_add-documents-list-container">
              <div v-if="documents.length > 0" class="vendor_add-documents-list">
                <div 
                  v-for="(document, index) in documents" 
                  :key="index" 
                  class="vendor_add-document-item"
                  :class="{ 'active': selectedDocumentIndex === index }"
                  @click="selectDocument(index)"
                >
                  <div class="vendor_add-document-info">
                    <div class="vendor_add-document-name">
                      <i class="bi bi-file-earmark"></i> {{ document.fileName }}
                    </div>
                    <div class="vendor_add-document-meta">
                      <span class="vendor_add-document-size">{{ formatFileSize(document.fileSize) }}</span>
                      <span class="vendor_add-document-date">{{ formatDate(document.uploadedAt) }}</span>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    @click.stop="removeVendorDocument(index)" 
                    class="vendor_add-document-delete-btn" 
                    title="Remove document"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
              <div v-else class="vendor_add-no-documents-message">
                <div class="vendor_add-no-documents-icon">📄</div>
                <p>No documents added yet</p>
                <p class="vendor_add-tooltip-subtitle">Click "Add Document" to upload files</p>
              </div>
            </div>
            
            <!-- Right Side: Preview -->
            <div class="vendor_add-document-preview-container">
              <div v-if="selectedDocumentIndex !== null && documents[selectedDocumentIndex]" class="vendor_add-document-preview">
                <div class="vendor_add-document-preview-header">
                  <h4>{{ documents[selectedDocumentIndex].fileName }}</h4>
                </div>
                <div class="vendor_add-document-preview-content">
                  <div v-if="loadingPreview" class="vendor_add-document-preview-loading">
                    <div class="vendor_add-document-preview-icon">
                      <i class="bi bi-arrow-repeat" style="font-size: 2rem; color: #0099cc; animation: spin 1s linear infinite;"></i>
                    </div>
                    <p>Loading preview...</p>
                  </div>
                  <div v-else-if="currentPreviewUrl && isImageFile(documents[selectedDocumentIndex])" class="vendor_add-document-preview-image">
                    <img :src="currentPreviewUrl" :alt="documents[selectedDocumentIndex].fileName" @error="handlePreviewError" />
                  </div>
                  <div v-else-if="currentPreviewUrl && isPdfFile(documents[selectedDocumentIndex])" class="vendor_add-document-preview-pdf">
                    <iframe :src="currentPreviewUrl" frameborder="0" @error="handlePreviewError"></iframe>
                  </div>
                  <div v-else-if="previewError" class="vendor_add-document-preview-error">
                    <div class="vendor_add-document-preview-icon">
                      <i class="bi bi-exclamation-triangle" style="font-size: 4rem; color: #dc3545;"></i>
                    </div>
                    <p>Failed to load preview</p>
                    <PrimaryButton
                      label="Open in New Window"
                      icon="bi bi-box-arrow-up-right"
                      @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                    />
                  </div>
                  <div v-else class="vendor_add-document-preview-unsupported">
                    <div class="vendor_add-document-preview-icon">
                      <i class="bi bi-file-earmark" style="font-size: 4rem; color: #ccc;"></i>
                    </div>
                    <p>Preview not available for this file type</p>
                    <PrimaryButton
                      label="Open in New Window"
                      icon="bi bi-box-arrow-up-right"
                      @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                    />
                  </div>
                </div>
              </div>
              <div v-else class="vendor_add-document-preview-empty">
                <div class="vendor_add-document-preview-icon">
                  <i class="bi bi-file-earmark-text" style="font-size: 4rem; color: #ccc;"></i>
                </div>
                <p>Select a document to preview</p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      </div>

      <div class="vendor_add-form-actions glass-tab-view__actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
        <PrimaryButton
          v-if="activeTab !== 'documents'"
          label="Next"
          icon="bi bi-arrow-right"
          :disabled="hasCurrentTabErrors"
          @click="handleNext"
        />
        <template v-if="activeTab === 'documents'">
          <PrimaryButton
            label="Save and New"
            icon="bi bi-plus-lg"
            @click="handleSaveAndNew"
          />
          <PrimaryButton
            label="Save and Close"
            icon="bi bi-check-lg"
            @click="handleSaveAndClose"
          />
        </template>
      </div>
    </form>
    <VendorDetailsSearch v-if="showSearchModal" @close="closeSearchModal" />
    <ManageCustomFields v-if="showCustomFieldsModal" @close="closeCustomFieldsModal" />
    <AddNewVendorGroup
      :visible="showAddGroupModal"
      :companyId="$route.params.id"
      @ok="onAddGroupOk"
      @cancel="showAddGroupModal = false"
    />
    <AddPreferredShippingMethod
      :visible="showAddShippingMethodModal"
      :companyId="$route.params.id"
      @ok="onAddShippingMethodOk"
      @cancel="showAddShippingMethodModal = false"
    />
    <AddNewPaymentTerm
      :visible="showAddPaymentTermModal"
      :companyId="$route.params.id"
      @ok="onAddPaymentTermOk"
      @cancel="showAddPaymentTermModal = false"
    />

    <!-- Error Modal -->
    <ErrorModal 
      :show="showErrorModal" 
      :title="errorTitle" 
      :message="errorMessage" 
      @close="closeErrorModal" 
    />

    <!-- Success Modal -->
    <SuccessModal 
      :show="showSuccessModal" 
      :title="successTitle" 
      :message="successMessage"
      button-text="Yes"
      secondary-button-text="No"
      @close="stayOnPageAfterSave"
      @secondary="leavePageAfterSave"
    />
  </div>
</template>

<script>
import '../styles/AddNewVendor.css';
import '../styles/add-new-vendor-glass.css';
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import logger from '@/utils/logger';
import ManageCustomFields from '@/components/ManageCustomFields.vue';
import AddressSelector from '@/components/AddressSelector.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import LabelInputFieldComponent from '@/modules/consultant/components/LabelInputField.vue';
import LabelCheckboxFieldComponent from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDescriptionFieldComponent from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDropdownComponent from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateFieldComponent from '@/modules/consultant/components/LabelDateField.vue';
import ApiService from '@/services/api.service.js';
import CompanyService from '@/services/company.service.js';
import vendorService from '@/services/vendor.service.js';
import AddNewVendorGroup from '@/modules/company/views/AddNewVendorGroup.vue';
import AddNewPaymentTerm from '@/modules/company/views/AddNewPaymentTerm.vue';
import AddPreferredShippingMethod from '@/modules/vendor/views/AddPreferredShippingMethod.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import { VENDOR_ROLES_FOR_SELECT } from '@/constants/vendorRoles.js';

export default {
  name: 'AddNewVendor',
  components: { 
    VendorDetailsSearch, 
    ManageCustomFields, 
    BrucdcumHeader, 
    AddNewVendorGroup, 
    AddNewPaymentTerm, 
    AddPreferredShippingMethod, 
    ErrorModal, 
    SuccessModal,
    TabNavigation,
    AddressSelector,
    PrimaryButton,
    SecondaryButton,
    LabelInputField: LabelInputFieldComponent,
    LabelCheckboxField: LabelCheckboxFieldComponent,
    LabelDescriptionField: LabelDescriptionFieldComponent,
    LabelDropdown: LabelDropdownComponent,
    LabelDateField: LabelDateFieldComponent,
  },
  data() {
    return {
      showErrorModal: false,
      errorTitle: 'Validation Error',
      errorMessage: '',
      validationErrors: [],
      errorTabs: [],
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      successAction: 'new', // 'new' or 'close'
      createdRecordId: null,
      activeTab: 'general',
      tabs: [
        { id: 'general', name: 'Basic Information' },
        { id: 'details', name: 'Payment & Settings' },
        { id: 'contacts', name: 'Contact Details' },
        { id: 'addresses', name: 'Address Information' },
        { id: 'documents', name: 'Documents' }
      ],
      vendorId: '',
      vendorName: '',
      vendorRole: '',
      active: true,
      vendorSince: this.getCurrentDate(),
      taxId: '',
      openingBalance: 0,
      balanceAsOf: this.getCurrentDate(),
      balance: 0,
      address: '',
      addressDetails: '',
      phone: '',
      fax: '',
      email: '',
      webpage: '',
      contacts: [],
      newContacts: [],
      notes: '',
      account: '',
      creditLimit: 0,
      shippingMethod: '',
      paymentMethod: '',
      paymentTerms: '',
      showErrors: false,
      accountNumber: '',
      bankName: '',
      bankBranch: '',
      bankAccountType: '',
      bankRoutingNumber: '',
      vendorGroup: '',
      taxRegistrationNumber: '',
      taxCode: '',
      serviceTaxRegistrationNo: '',
      showSearchModal: false,
      showCustomFieldsModal: false,
      addresses: [],
      newAddresses: [],
      editingAddressIdx: null,
      editingContactIdx: null,
      selectedAddressType: '',
      accounts: [],
      filteredAccounts: [],
      paymentTermsList: [],
      vendorGroups: [],
      showAddGroupModal: false,
      previousVendorGroup: '',
      showAddPaymentTermModal: false,
      showAddShippingMethodModal: false,
      shippingMethodsList: [],
      previousShippingMethod: '',
      previousPaymentTerm: '',
      vendorIdExists: false,
      checkingVendorId: false,
      vendorIdCheckTimeout: null,
      contactEmailErrors: {},
      checkingContactEmails: {},
      contactEmailCheckTimeouts: {},
      documents: [],
      selectedDocumentIndex: null,
      documentPreviewUrls: {}, // Cache for preview URLs
      currentPreviewUrl: '', // Current preview URL for selected document
      loadingPreview: false,
      previewError: false,
    };
  },
  computed: {
    availableAddressTypes() {
      const types = this.addresses.map(addr => addr.type).filter(Boolean);
      return [...new Set(types)];
    },
    hasBillingAddress() {
      return this.addresses.some(addr => addr.type === 'Billing');
    },
    hasShippingAddress() {
      return this.addresses.some(addr => addr.type === 'Shipping');
    },
    selectedVendorAddressDetails() {
      const selected = this.addresses.find(a => a.type === this.selectedAddressType);
      if (!selected) return '';
      return [selected.address1, selected.address2, selected.city, selected.state, selected.postalCode, selected.country].filter(Boolean).join('\n');
    },
    hasCurrentTabErrors() {
      // Check if current tab has validation errors
      if (this.activeTab === 'general') {
        return (!this.vendorId || this.vendorId.trim() === '') ||
               this.vendorIdExists ||
               (!this.vendorName || this.vendorName.trim() === '') ||
               (this.vendorSince && !this.validateDate('vendorSince')) ||
               (this.balanceAsOf && !this.validateDate('balanceAsOf')) ||
               (this.openingBalance < 0);
      }
      
      if (this.activeTab === 'details') {
        // Payment Terms validation: must not be empty string, null, undefined, or __add_new__
        // Use == null to check for both null and undefined in one check
        if (this.paymentTerms == null || this.paymentTerms === '' || this.paymentTerms === '__add_new__') {
          return true; // Has errors - button disabled
        }
        
        // Payment Method validation: must not be empty string, null, or undefined
        if (this.paymentMethod == null || this.paymentMethod === '') {
          return true; // Has errors - button disabled
        }
        
        // Only check for negative credit limit (not blocking if 0 or positive)
        if (this.creditLimit < 0) {
          return true;
        }
        
        // Only check account number format if it's provided and not empty
        if (this.accountNumber && this.accountNumber.trim() !== '' && !/^\d+$/.test(this.accountNumber)) {
          return true;
        }
        
        // No errors - button should be enabled
        return false;
      }
      
      if (this.activeTab === 'contacts') {
        const allContacts = [...this.contacts, ...this.newContacts];
        if (allContacts.length === 0) {
          return false;
        }

        if (!allContacts.some(contact => contact.primary)) {
          return true;
        }

        return allContacts.some(contact => {
          if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) return true;
          if (contact.phone && !/^[\d\s-+()]+$/.test(contact.phone)) return true;
          return false;
        });
      }
      
      if (this.activeTab === 'addresses') {
        const allAddresses = [...this.addresses, ...this.newAddresses];
        if (allAddresses.length === 0) {
          return false;
        }

        return allAddresses.some(address => {
          const hasAnyField = address.address1 || address.city || address.state || address.postalCode || address.country;
          if (address.type === 'Billing' && hasAnyField) {
            return !address.address1 || !address.city || !address.state || !address.postalCode || !address.country;
          }
          return false;
        });
      }
      
      return false;
    },
    paymentTermsModel: {
      get() {
        return this.paymentTerms === '' || this.paymentTerms == null ? '' : String(this.paymentTerms);
      },
      set(v) {
        this.paymentTerms = v;
      },
    },
    accountModel: {
      get() {
        return this.account === '' || this.account == null ? '' : String(this.account);
      },
      set(v) {
        this.account = v;
      },
    },
    shippingMethodModel: {
      get() {
        return this.shippingMethod === '' || this.shippingMethod == null ? '' : String(this.shippingMethod);
      },
      set(v) {
        this.shippingMethod = v;
      },
    },
    vendorGroupModel: {
      get() {
        return this.vendorGroup === '' || this.vendorGroup == null ? '' : String(this.vendorGroup);
      },
      set(v) {
        this.vendorGroup = v;
      },
    },
    paymentTermsDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.paymentTermsList || []).map((term) => ({
          value: String(term.id),
          label: `${term.name} (${term.description})`,
        })),
        { value: '__add_new__', label: 'Add New Payment Term' },
      ];
    },
    paymentMethodDropdownOptions() {
      const methods = [
        'Cash',
        'Cheque',
        'Credit Card',
        'Direct Deposit',
        'Master card',
        'Online Banking',
        'Standing Order',
        'Switch/Maestro',
      ];
      return [
        { value: '', label: 'Select' },
        ...methods.map((method) => ({ value: method, label: method })),
      ];
    },
    bankAccountTypeDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        { value: 'Checking', label: 'Checking' },
        { value: 'Savings', label: 'Savings' },
      ];
    },
    accountDropdownOptions() {
      return [
        { value: '', label: 'Select account' },
        ...(this.filteredAccounts || []).map((acc) => ({
          value: String(acc.id),
          label: `${acc.accountNumber} - ${acc.accountName} - ${acc.accountType}`,
        })),
        { value: '__add_new__', label: 'Add New Account' },
      ];
    },
    shippingMethodDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.shippingMethodsList || []).map((method) => ({
          value: String(method.id),
          label: method.name,
        })),
        { value: '__add_new__', label: 'Add New Shipping Method' },
      ];
    },
    vendorGroupDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.vendorGroups || []).map((group) => ({
          value: String(group.id),
          label: group.name,
        })),
        { value: '__create_new__', label: 'Add New Group' },
      ];
    },
    vendorRoleDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...VENDOR_ROLES_FOR_SELECT,
      ];
    },
    taxCodeDropdownOptions() {
      return [{ value: '', label: 'Select' }];
    },
  },
  methods: {
    resolveSelectValue(eventOrValue) {
      if (eventOrValue && typeof eventOrValue === 'object' && eventOrValue.target) {
        return eventOrValue.target.value;
      }
      return eventOrValue;
    },
    addressTypeDropdownOptions(idx, isNew) {
      const types = this.getAvailableAddressTypes(idx, isNew);
      return [
        { value: '', label: 'Select Type' },
        ...types.map((type) => ({ value: type, label: type })),
      ];
    },
    handlePaymentTermChangeAndClearError(eventOrValue) {
      this.handlePaymentTermChange(eventOrValue);
      this.clearPaymentTermsError();
    },
    handleTabChange(newTab) {
      if (newTab === 'contacts') {
        this.initializeDefaultContacts();
      } else if (newTab === 'addresses') {
        this.initializeDefaultAddresses();
      }
    },
    clearVendorIdError() {
      // Clear vendor ID error when user starts typing
    },
    
    async handleVendorIdInput() {
      this.vendorIdExists = false;
      
      // Only check if vendor ID is not empty and company ID exists
      if (!this.vendorId || !this.vendorId.trim()) {
        return;
      }
      
      const companyId = this.$route.params.id;
      if (!companyId) {
        return;
      }
      
      // Debounce the API call
      clearTimeout(this.vendorIdCheckTimeout);
      this.checkingVendorId = true;
      
      this.vendorIdCheckTimeout = setTimeout(async () => {
        try {
          const response = await vendorService.checkVendorIdExists(this.vendorId.trim(), companyId);
          this.vendorIdExists = response.data.exists;
        } catch (error) {
          logger.error('Error checking vendor ID:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingVendorId = false;
        }
      }, 500); // 500ms debounce
    },
    clearVendorNameError() {
      // Clear vendor name error when user starts typing
    },

    getCurrentDate() {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    },
    openSearchModal() {
      this.showSearchModal = true;
    },
    closeSearchModal() {
      this.showSearchModal = false;
    },
    openCustomFieldsModal() {
      this.showCustomFieldsModal = true;
    },
    closeCustomFieldsModal() {
      this.showCustomFieldsModal = false;
    },
    addContactRow() {
      if (this.newContacts.length === 0) {
        this.newContacts.push({
          primary: false,
          name: '',
          title: '',
          phone: '',
          email: ''
        });
      } else {
        // Show error in ErrorModal instead of toast
        this.errorTitle = 'Cannot Add Contact';
        this.errorMessage = 'Please save or cancel the current new contact before adding another.\n\nYou can only add one contact at a time to ensure proper data entry and validation.';
        this.showErrorModal = true;
      }
    },
    async saveContact(nidx) {
      const contact = this.newContacts[nidx];
      
      // Validate all required fields
      const errors = [];
      if (!contact.name || !contact.name.trim()) {
        errors.push('Contact Name is required');
      }
      if (!contact.title || !contact.title.trim()) {
        errors.push('Title is required');
      }
      if (!contact.phone || !contact.phone.trim()) {
        errors.push('Business Phone is required');
      } else if (!/^[\d\s-+()]+$/.test(contact.phone)) {
        errors.push('Invalid phone number format (allowed: digits, spaces, -, +, parentheses)');
      }
      if (!contact.email || !contact.email.trim()) {
        errors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        errors.push('Invalid email format');
      }

      if (errors.length > 0) {
        this.errorTitle = 'Contact Validation Error';
        this.errorMessage = errors.join('\n\n');
        this.showErrorModal = true;
        return;
      }
      
      // Validate email uniqueness (immediate check, no debounce)
      await this.validateContactEmail(contact.email, nidx, true, true);
      if (this.contactEmailErrors[`new-contact-${nidx}`]) {
        this.showError('Contact Email Validation Error', this.contactEmailErrors[`new-contact-${nidx}`]);
        return;
      }
      
      // Handle primary contact logic
      if (this.contacts.length === 0) {
        // First contact must be primary
        contact.primary = true;
      } else if (contact.primary) {
        // If this contact is being saved as primary, uncheck all others
        this.contacts.forEach(c => c.primary = false);
        this.newContacts.forEach((c, i) => {
          if (i !== nidx) c.primary = false;
        });
      } else if (this.getNumberOfPrimaryContacts() === 0) {
        // If no primary contacts exist, force this one to be primary
        contact.primary = true;
      }
      
      this.contacts.push(contact);
      this.newContacts.splice(nidx, 1);
      
      // Update phone and email if this is the primary contact
      if (contact.primary) {
        this.phone = contact.phone || '';
        this.email = contact.email || '';
      }
    },
    cancelContact(nidx) {
      this.newContacts.splice(nidx, 1);
    },
    addNewAddressRow() {
      if (this.newAddresses.length === 0) {
        this.newAddresses.push({ 
          type: '', 
          address1: '', 
          address2: '', 
          city: '', 
          state: '', 
          postalCode: '', 
          country: '' 
        });
      } else {
        // Show error in ErrorModal instead of toast
        this.errorTitle = 'Cannot Add Address';
        this.errorMessage = 'Please save or cancel the current new address before adding another.\n\nYou can only add one address at a time to ensure proper data entry and validation.';
        this.showErrorModal = true;
      }
    },
    saveNewAddress(idx) {
      const na = this.newAddresses[idx];
      
      // Validate all required fields
      const errors = [];
      if (!na.type) {
        errors.push('Address Type is required');
      }
      if (!na.address1 || !na.address1.trim()) {
        errors.push('Address Line 1 is required');
      }
      if (!na.city || !na.city.trim()) {
        errors.push('City is required');
      }
      if (!na.state || !na.state.trim()) {
        errors.push('State is required');
      }
      if (!na.postalCode || !na.postalCode.trim()) {
        errors.push('Postal Code is required');
      }
      if (!na.country || !na.country.trim()) {
        errors.push('Country is required');
      }

      if (errors.length > 0) {
        this.errorTitle = 'Address Validation Error';
        this.errorMessage = errors.join('\n\n');
        this.showErrorModal = true;
        return;
      }

      this.addresses = [...this.addresses, na];
      this.newAddresses.splice(idx, 1);
      this.selectedAddressType = na.type;
      this.fillFromSelectedAddress();
      this.$nextTick(() => {
        this.autoResizeAddressTextarea();
      });
    },
    cancelNewAddress(idx) {
      this.newAddresses.splice(idx, 1);
    },
    deleteAddress(idx) {
      this.addresses.splice(idx, 1);
    },
    editAddress(idx) {
      this.editingAddressIdx = idx;
    },
    updateAddress(idx) {
      const address = this.addresses[idx];
      
      // Validate all required fields
      const errors = [];
      if (!address.type) {
        errors.push('Address Type is required');
      }
      if (!address.address1 || !address.address1.trim()) {
        errors.push('Address Line 1 is required');
      }
      if (!address.city || !address.city.trim()) {
        errors.push('City is required');
      }
      if (!address.state || !address.state.trim()) {
        errors.push('State is required');
      }
      if (!address.postalCode || !address.postalCode.trim()) {
        errors.push('Postal Code is required');
      }
      if (!address.country || !address.country.trim()) {
        errors.push('Country is required');
      }

      if (errors.length > 0) {
        this.errorTitle = 'Address Validation Error';
        this.errorMessage = errors.join('\n\n');
        this.showErrorModal = true;
        return;
      }

      this.editingAddressIdx = null;
      
      // Update the address details if this is the selected address type
      if (this.selectedAddressType === address.type) {
        this.fillFromSelectedAddress();
      }
      
      this.$toast && this.$toast.success('Address updated successfully.');
    },
    cancelEditAddress() {
      this.editingAddressIdx = null;
    },
    editContact(idx) {
      this.editingContactIdx = idx;
    },
    async updateContact() {
      const contact = this.contacts[this.editingContactIdx];
      
      // Validate all required fields
      const errors = [];
      if (!contact.name || !contact.name.trim()) {
        errors.push('Contact Name is required');
      }
      if (!contact.title || !contact.title.trim()) {
        errors.push('Title is required');
      }
      if (!contact.phone || !contact.phone.trim()) {
        errors.push('Business Phone is required');
      } else if (!/^[\d\s-+()]+$/.test(contact.phone)) {
        errors.push('Invalid phone number format (allowed: digits, spaces, -, +, parentheses)');
      }
      if (!contact.email || !contact.email.trim()) {
        errors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        errors.push('Invalid email format');
      }

      if (errors.length > 0) {
        this.errorTitle = 'Contact Validation Error';
        this.errorMessage = errors.join('\n\n');
        this.showErrorModal = true;
        return;
      }
      
      // Validate email uniqueness (immediate check, no debounce)
      await this.validateContactEmail(contact.email, this.editingContactIdx, false, true);
      if (this.contactEmailErrors[`contact-${this.editingContactIdx}`]) {
        this.showError('Contact Email Validation Error', this.contactEmailErrors[`contact-${this.editingContactIdx}`]);
        return;
      }
      
      // Update phone and email if this is the primary contact
      if (contact.primary) {
        this.phone = contact.phone || '';
        this.email = contact.email || '';
      }
      
      this.editingContactIdx = null;
      this.$toast && this.$toast.success('Contact updated successfully.');
    },
    cancelEditContact() {
      this.editingContactIdx = null;
    },
    deleteContact(idx) {
      const wasPrimary = this.contacts[idx] && this.contacts[idx].primary;
      this.contacts.splice(idx, 1);
      if (wasPrimary) {
        this.phone = '';
        this.email = '';
      }
    },
    setPrimaryContact(idx) {
      const contact = this.contacts[idx];
      
      // If unchecking the only primary contact, prevent it
      if (!contact.primary && this.getNumberOfPrimaryContacts() === 1) {
        contact.primary = true;
        return;
      }
      
      // If checking this contact as primary, uncheck all others
      if (contact.primary) {
        this.newContacts.forEach(c => c.primary = false);
      this.contacts.forEach((c, i) => {
        if (i !== idx) c.primary = false;
      });
      }
      
      this.fillFromContact(idx);
    },
    fillFromContact(idx) {
      const contact = this.contacts[idx];
      if (contact) {
        this.phone = contact.phone || '';
        this.email = contact.email || '';
      }
    },
    isContactPrimaryCheckboxDisabled(idx) {
      const totalContacts = this.contacts.length + this.newContacts.length;
      const contact = this.contacts[idx];
      
      // If it's the only contact, it must be primary
      if (totalContacts === 1) return true;
      
      // If this is the only primary contact and it's already checked, disable it
      if (contact.primary && this.getNumberOfPrimaryContacts() === 1) return true;
      
      return false;
    },
    
    isNewContactPrimaryCheckboxDisabled(nidx) {
      const totalContacts = this.contacts.length + this.newContacts.length;
      const contact = this.newContacts[nidx];
      
      // If it's the only contact, it must be primary
      if (totalContacts === 1) return true;
      
      // If this is the only primary contact and it's already checked, disable it
      if (contact.primary && this.getNumberOfPrimaryContacts() === 1) return true;
      
      return false;
    },
    
    getNumberOfPrimaryContacts() {
      const primaryExistingContacts = this.contacts.filter(c => c.primary).length;
      const primaryNewContacts = this.newContacts.filter(c => c.primary).length;
      return primaryExistingContacts + primaryNewContacts;
    },
    
    setPrimaryContactNew(nidx) {
      const contact = this.newContacts[nidx];
      
      // If unchecking the only primary contact, prevent it
      if (!contact.primary && this.getNumberOfPrimaryContacts() === 1) {
        contact.primary = true;
        return;
      }
      
      // If checking this contact as primary, uncheck all others
      if (contact.primary) {
        this.contacts.forEach(c => c.primary = false);
      this.newContacts.forEach((c, i) => {
        if (i !== nidx) c.primary = false;
      });
      }
         },
     handleBalanceAsOfChange(date) {
       this.balanceAsOf = date;
     },
    handleVendorSinceChange(date) {
      this.vendorSince = date;
      this.validateDateField('vendorSince');
    },
    
    validateDate(field) {
      const value = this[field];
      if (!value) return true;
      
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!dateRegex.test(value)) return false;
      
      const parts = value.split('/');
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && 
             date.getMonth() === month - 1 && 
             date.getDate() === day;
    },

    validateDateField(field) {
      if (this[field] && !this.validateDate(field)) {
        this.$toast?.error(`Please enter a valid date in MM/DD/YYYY format for ${field}`);
      }
    },
    switchToContactsTab() {
      this.activeTab = 'contacts';
      this.initializeDefaultContacts();
    },
    
    initializeDefaultContacts() {
      // If no contacts exist, add one default contact row
      if (this.contacts.length === 0 && this.newContacts.length === 0) {
        const defaultContact = { 
          name: '', 
          title: '', 
          phone: '', 
          email: '', 
          primary: true  // First contact must be primary
        };
        this.newContacts.push(defaultContact);
        
        // Set the main vendor contact info since this is primary
        this.phone = '';
        this.email = '';
      }
    },
    switchToAddressesTab() {
      this.activeTab = 'addresses';
      this.initializeDefaultAddresses();
    },
    
    initializeDefaultAddresses() {
      // If no addresses exist, add one default address row with Billing type
      if (this.addresses.length === 0 && this.newAddresses.length === 0) {
        this.newAddresses.push({ type: 'Billing', address1: '', address2: '', city: '', state: '', postalCode: '', country: '' });
      }
    },
    handleNext() {
      // Set showErrors to true to display validation messages
      this.showErrors = true;
      this.clearValidationErrors();
      
      // Validate only current tab
      const hasErrors = this.validateCurrentTab();
      
      if (hasErrors) {
        this.showAllErrors();
        return;
      }

      // If no errors in current tab, navigate to next tab
      this.navigateToNextTab();
    },
    validateCurrentTab() {
      let hasErrors = false;
      
      if (this.activeTab === 'general') {
        // Only validate vendor ID, vendor name and date formats if provided
        if (!this.vendorId || this.vendorId.trim() === '') {
          this.addValidationError('Vendor ID is required.', 'general');
          hasErrors = true;
        }
        if (this.vendorIdExists) {
          this.addValidationError('This Vendor ID already exists in this company.', 'general');
          hasErrors = true;
        }
        if (!this.vendorName || this.vendorName.trim() === '') {
          this.addValidationError('Vendor Name is required.', 'general');
          hasErrors = true;
        }
        if (this.openingBalance < 0) {
          this.addValidationError('Opening Balance cannot be negative.', 'general');
          hasErrors = true;
        }
        if (this.vendorSince && !this.validateDate('vendorSince')) {
          this.addValidationError('Please enter a valid date in MM/DD/YYYY format for Vendor Since', 'general');
          hasErrors = true;
        }
        if (this.balanceAsOf && !this.validateDate('balanceAsOf')) {
          this.addValidationError('Please enter a valid date in MM/DD/YYYY format for Balance as of', 'general');
          hasErrors = true;
        }
      }
      
      else if (this.activeTab === 'details') {
        // Validate required fields
        if (!this.paymentTerms) {
          this.addValidationError('Payment Terms is required.', 'details');
          hasErrors = true;
        }
        if (!this.paymentMethod) {
          this.addValidationError('Payment Method is required.', 'details');
          hasErrors = true;
        }
        // Only validate format of optional fields if they are provided
        if (this.creditLimit < 0) {
          this.addValidationError('Credit Limit cannot be negative.', 'details');
          hasErrors = true;
        }
        if (this.accountNumber && !/^\d+$/.test(this.accountNumber)) {
          this.addValidationError('Account Number must contain only digits.', 'details');
          hasErrors = true;
        }
      }
      
      else if (this.activeTab === 'contacts') {
        const allContacts = [...this.contacts, ...this.newContacts];

        if (allContacts.length > 0) {
          if (!allContacts.some(contact => contact.primary)) {
            this.addValidationError('At least one contact must be marked as primary.', 'contacts');
            hasErrors = true;
          }

          allContacts.forEach((contact, index) => {
            if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
              this.addValidationError(`Contact ${index + 1}: Invalid email format.`, 'contacts');
              hasErrors = true;
            }
            if (contact.phone && !/^[\d\s-+()]+$/.test(contact.phone)) {
              this.addValidationError(`Contact ${index + 1}: Invalid phone number format.`, 'contacts');
              hasErrors = true;
            }
          });
        }
      }
      
      else if (this.activeTab === 'addresses') {
        const allAddresses = [...this.addresses, ...this.newAddresses];

        allAddresses.forEach(address => {
          const hasAnyField = address.address1 || address.city || address.state || address.postalCode || address.country;
          if (address.type === 'Billing' && hasAnyField) {
            if (!address.address1 || !address.city || !address.state || !address.postalCode || !address.country) {
              this.addValidationError(`Billing Address: All fields are required.`, 'addresses');
              hasErrors = true;
            }
          }
        });
      }
      
      return hasErrors;
    },
    navigateToNextTab() {
      const tabs = ['general', 'details', 'contacts', 'addresses', 'documents'];
      const currentIndex = tabs.indexOf(this.activeTab);
      
      if (currentIndex < tabs.length - 1) {
        this.activeTab = tabs[currentIndex + 1];
      }
    },
     autoResizeAddressTextarea() {
      this.$nextTick(() => {
        const textarea = this.$refs.addressDetailsTextarea;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = textarea.scrollHeight + 'px';
        }
      });
    },
    getAvailableAddressTypes(idx, isNew) {
      const allTypes = ["Billing", "Shipping", "Other"];
      // Collect used types from addresses (excluding the current row)
      let usedTypes = this.addresses.map((a, i) => (isNew ? a.type : (i !== idx ? a.type : null))).filter(Boolean);
      // Also exclude types being used in other newAddresses (for add rows)
      if (isNew) {
        usedTypes = usedTypes.concat(this.newAddresses.map((a, i) => (i !== idx ? a.type : null)).filter(Boolean));
      }
      // Always allow the current row's type (for edit rows)
      let currentType = isNew ? this.newAddresses[idx]?.type : this.addresses[idx]?.type;
      return allTypes.filter(type => !usedTypes.includes(type) || type === currentType);
    },
    handleAccountChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.navigateToNewAccount();
      }
    },
    navigateToNewAccount() {
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/new-account`);
    },
    async fetchVendorGroups() {
      try {
        const companyId = this.$route.params.id;
        if (!companyId) return;
        const groups = await CompanyService.getAllVendorGroups(companyId);
        this.vendorGroups = Array.isArray(groups) ? groups : [];
      } catch (err) {
        this.vendorGroups = [];
      }
    },
    handleVendorGroupChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__create_new__') {
        this.showAddGroupModal = true;
        this.$nextTick(() => {
          this.vendorGroup = this.previousVendorGroup;
        });
      } else {
        this.vendorGroup = selected;
        this.previousVendorGroup = selected;
      }
    },
    async onAddGroupOk(group) {
      this.showAddGroupModal = false
      await this.fetchVendorGroups()
      if (group && group.id) {
        this.vendorGroup = group.id;
        this.previousVendorGroup = group.id;
        this.$toast && this.$toast.success('Vendor group created!');
      }
    },
    handlePaymentTermChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddPaymentTermModal = true;
        this.$nextTick(() => {
          this.paymentTerms = this.previousPaymentTerm;
        });
      } else {
        this.paymentTerms = selected;
        this.previousPaymentTerm = selected;
      }
    },
    clearPaymentTermsError() {
      if (this.showErrors && this.paymentTerms) {
        // Errors will be cleared when field has a value
      }
    },
    clearPaymentMethodError() {
      if (this.showErrors && this.paymentMethod) {
        // Errors will be cleared when field has a value
      }
    },
    async onAddPaymentTermOk(newTerm) {
      this.showAddPaymentTermModal = false
      await this.fetchPaymentTerms()
      if (newTerm && newTerm.id) {
        this.paymentTerms = newTerm.id;
        this.previousPaymentTerm = newTerm.id;
        this.$toast && this.$toast.success('Payment term created!');
      }
    },
    async fetchPaymentTerms() {
      const companyId = this.$route.params.id;
      const termsRes = await ApiService.get(`/company/payment-terms?companyId=${companyId}`);
      this.paymentTermsList = Array.isArray(termsRes.data.paymentTerms) ? termsRes.data.paymentTerms : [];
    },
    handleShippingMethodChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddShippingMethodModal = true;
        this.$nextTick(() => {
          this.shippingMethod = this.previousShippingMethod;
        });
      } else {
        this.shippingMethod = selected;
        this.previousShippingMethod = selected;
      }
    },
    async onAddShippingMethodOk(newMethod) {
      this.showAddShippingMethodModal = false;
      await this.fetchShippingMethods();
      if (newMethod && newMethod.id) {
        this.shippingMethod = newMethod.id;
        this.previousShippingMethod = newMethod.id;
        this.$toast && this.$toast.success('Shipping method created!');
      }
    },
    async fetchShippingMethods() {
      const companyId = this.$route.params.id;
      const res = await ApiService.get(`/vendor/preferred-shipping-methods?companyId=${companyId}`);
      this.shippingMethodsList = Array.isArray(res.data) ? res.data : [];
    },
    async handleSaveAndClose() {
      this.showErrors = true;
      this.clearValidationErrors();
      
      // Only validate vendor ID and vendor name from general tab
      if (!this.vendorId || this.vendorId.trim() === '') {
        this.addValidationError('Vendor ID is required.', 'general');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Vendor ID is required.';
        this.showErrorModal = true;
        return;
      }
      if (this.vendorIdExists) {
        this.addValidationError('This Vendor ID already exists in this company.', 'general');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'This Vendor ID already exists in this company.';
        this.showErrorModal = true;
        return;
      }
      if (!this.vendorName || this.vendorName.trim() === '') {
        this.addValidationError('Vendor Name is required.', 'general');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Vendor Name is required.';
        this.showErrorModal = true;
        return;
      }
      if (!this.paymentTerms) {
        this.addValidationError('Payment Terms is required.', 'details');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Payment Terms is required.';
        this.showErrorModal = true;
        return;
      }
      if (!this.paymentMethod) {
        this.addValidationError('Payment Method is required.', 'details');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Payment Method is required.';
        this.showErrorModal = true;
        return;
      }

      const allContacts = [...this.contacts, ...this.newContacts];
      const hasFilledContact = allContacts.some(contact =>
        (contact.name && contact.name.trim()) ||
        (contact.email && contact.email.trim()) ||
        (contact.phone && contact.phone.trim())
      );
      if (hasFilledContact && !allContacts.some(contact => contact.primary)) {
        this.addValidationError('At least one contact must be marked as primary.', 'contacts');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'At least one contact must be marked as primary.';
        this.showErrorModal = true;
        return;
      }

      this.fillFromSelectedAddress();
      const payload = {
        vendorId: this.vendorId,
        vendorName: this.vendorName,
        vendorRole: this.vendorRole || null,
        active: this.active,
        vendorSince: this.vendorSince,
        taxId: this.taxId,
        openingBalance: this.openingBalance,
        balanceAsOf: this.balanceAsOf,
        balance: this.balance,
        fax: this.fax,
        webpage: this.webpage,
        notes: this.notes,
        accountId: this.account || null,
        creditLimit: this.creditLimit,
        shippingMethodId: this.shippingMethod || null,
        paymentMethod: this.paymentMethod,
        paymentTermId: this.paymentTerms || null,
        accountNumber: this.accountNumber,
        bankName: this.bankName,
        bankBranch: this.bankBranch,
        bankAccountType: this.bankAccountType || null,
        bankRoutingNumber: this.bankRoutingNumber,
        vendorGroupId: this.vendorGroup || null,
        taxRegistrationNumber: this.taxRegistrationNumber,
        taxCode: this.taxCode,
        serviceTaxRegistrationNo: this.serviceTaxRegistrationNo,
        companyId: this.$route.params.id,
        contacts: this.contacts,
        addresses: this.addresses,
        address: this.selectedAddressType,
        addressDetails: this.addressDetails,
        phone: this.phone,
        email: this.email,
      };
      
      // Validate vendor email before saving
      if (this.email && this.email.trim()) {
        const emailCheck = await vendorService.checkVendorEmailExists(this.email.trim(), this.$route.params.id);
        if (emailCheck.data.exists) {
          let errorMessage = '';
          if (emailCheck.data.existsAsConsultant) {
            errorMessage = 'This email address already exists for a consultant in this company.';
          } else if (emailCheck.data.existsAsContact) {
            errorMessage = 'This email address already exists for a customer contact in this company.';
          } else if (emailCheck.data.existsAsVendor) {
            errorMessage = 'This email address already exists for another vendor in this company.';
          } else {
            errorMessage = 'This email address is already in use.';
          }
          this.showError('Contact Email Validation Error', errorMessage);
          return;
        }
      }
      
      try {
        const response = await ApiService.post('/vendor/vendors', payload);
        const savedVendor = response.data;
        
        // Upload documents if any
        if (this.documents.length > 0 && savedVendor && savedVendor.id) {
          await this.uploadDocuments(savedVendor.id);
        }
        
        this.createdRecordId = savedVendor && savedVendor.id ? savedVendor.id : null;
        this.showSuccess(
          'Success',
          'Vendor created successfully.\nStay on this page to view the details?',
          'close'
        );
      } catch (err) {
        let errorMessage = err.response?.data?.error || 'Failed to create vendor';
        
        // Handle email-related errors
        if (errorMessage.includes('email') || errorMessage.includes('Email')) {
          if (errorMessage.includes('consultant')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for a consultant in this company.');
          } else if (errorMessage.includes('customer contact') || errorMessage.includes('contact')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for a customer contact in this company.');
          } else if (errorMessage.includes('vendor')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for another vendor in this company.');
          } else {
            this.showError('Contact Email Validation Error', errorMessage);
          }
          return;
        }
        
        this.showError('Save Failed', errorMessage);
      }
    },
    async handleSaveAndNew() {
      this.showErrors = true;
      this.clearValidationErrors();
      
      // Only validate vendor ID and vendor name from general tab
      if (!this.vendorId || this.vendorId.trim() === '') {
        this.addValidationError('Vendor ID is required.', 'general');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Vendor ID is required.';
        this.showErrorModal = true;
        return;
      }
      if (this.vendorIdExists) {
        this.addValidationError('This Vendor ID already exists in this company.', 'general');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'This Vendor ID already exists in this company.';
        this.showErrorModal = true;
        return;
      }
      if (!this.vendorName || this.vendorName.trim() === '') {
        this.addValidationError('Vendor Name is required.', 'general');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Vendor Name is required.';
        this.showErrorModal = true;
        return;
      }
      if (!this.paymentTerms) {
        this.addValidationError('Payment Terms is required.', 'details');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Payment Terms is required.';
        this.showErrorModal = true;
        return;
      }
      if (!this.paymentMethod) {
        this.addValidationError('Payment Method is required.', 'details');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Payment Method is required.';
        this.showErrorModal = true;
        return;
      }

      const allContacts = [...this.contacts, ...this.newContacts];
      const hasFilledContact = allContacts.some(contact =>
        (contact.name && contact.name.trim()) ||
        (contact.email && contact.email.trim()) ||
        (contact.phone && contact.phone.trim())
      );
      if (hasFilledContact && !allContacts.some(contact => contact.primary)) {
        this.addValidationError('At least one contact must be marked as primary.', 'contacts');
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'At least one contact must be marked as primary.';
        this.showErrorModal = true;
        return;
      }

      this.fillFromSelectedAddress();
      const payload = {
        vendorId: this.vendorId,
        vendorName: this.vendorName,
        vendorRole: this.vendorRole || null,
        active: this.active,
        vendorSince: this.vendorSince,
        taxId: this.taxId,
        openingBalance: this.openingBalance,
        balanceAsOf: this.balanceAsOf,
        balance: this.balance,
        fax: this.fax,
        webpage: this.webpage,
        notes: this.notes,
        accountId: this.account || null,
        creditLimit: this.creditLimit,
        shippingMethodId: this.shippingMethod || null,
        paymentMethod: this.paymentMethod,
        paymentTermId: this.paymentTerms || null,
        accountNumber: this.accountNumber,
        bankName: this.bankName,
        bankBranch: this.bankBranch,
        bankAccountType: this.bankAccountType || null,
        bankRoutingNumber: this.bankRoutingNumber,
        vendorGroupId: this.vendorGroup || null,
        taxRegistrationNumber: this.taxRegistrationNumber,
        taxCode: this.taxCode,
        serviceTaxRegistrationNo: this.serviceTaxRegistrationNo,
        companyId: this.$route.params.id,
        contacts: this.contacts,
        addresses: this.addresses,
        address: this.selectedAddressType,
        addressDetails: this.addressDetails,
        phone: this.phone,
        email: this.email,
      };
      
      // Validate vendor email before saving
      if (this.email && this.email.trim()) {
        const emailCheck = await vendorService.checkVendorEmailExists(this.email.trim(), this.$route.params.id);
        if (emailCheck.data.exists) {
          let errorMessage = '';
          if (emailCheck.data.existsAsConsultant) {
            errorMessage = 'This email address already exists for a consultant in this company.';
          } else if (emailCheck.data.existsAsContact) {
            errorMessage = 'This email address already exists for a customer contact in this company.';
          } else if (emailCheck.data.existsAsVendor) {
            errorMessage = 'This email address already exists for another vendor in this company.';
          } else {
            errorMessage = 'This email address is already in use.';
          }
          this.showError('Contact Email Validation Error', errorMessage);
          return;
        }
      }
      
      try {
        const response = await ApiService.post('/vendor/vendors', payload);
        const savedVendor = response.data;
        
        // Upload documents if any
        if (this.documents.length > 0 && savedVendor && savedVendor.id) {
          await this.uploadDocuments(savedVendor.id);
        }
        
        this.createdRecordId = savedVendor && savedVendor.id ? savedVendor.id : null;
        this.showSuccess(
          'Success',
          'Vendor created successfully.\nStay on this page to create another?',
          'new'
        );
      } catch (err) {
        let errorMessage = err.response?.data?.error || 'Failed to create vendor';
        
        // Handle email-related errors
        if (errorMessage.includes('email') || errorMessage.includes('Email')) {
          if (errorMessage.includes('consultant')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for a consultant in this company.');
          } else if (errorMessage.includes('customer contact') || errorMessage.includes('contact')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for a customer contact in this company.');
          } else if (errorMessage.includes('vendor')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for another vendor in this company.');
          } else {
            this.showError('Contact Email Validation Error', errorMessage);
          }
          return;
        }
        
        this.showError('Save Failed', errorMessage);
      }
    },
         resetForm() {
       this.vendorId = '';
       this.vendorName = '';
       this.vendorRole = '';
       this.active = true;
       this.vendorSince = this.getCurrentDate();
       this.taxId = '';
       this.openingBalance = 0;
       this.balanceAsOf = this.getCurrentDate();
       this.balance = 0;
      this.fax = '';
      this.webpage = '';
      this.notes = '';
      this.account = '';
      this.creditLimit = 0;
      this.shippingMethod = '';
      this.paymentMethod = '';
      this.paymentTerms = '';
      this.showErrors = false;
      this.accountNumber = '';
      this.bankName = '';
      this.bankBranch = '';
      this.bankAccountType = '';
      this.bankRoutingNumber = '';
      this.vendorGroup = '';
      this.taxRegistrationNumber = '';
      this.taxCode = '';
      this.serviceTaxRegistrationNo = '';
      this.contacts = [];
      this.newContacts = [];
      this.addresses = [];
      this.newAddresses = [];
      this.selectedAddressType = '';
      this.editingAddressIdx = null;
      this.editingContactIdx = null;
      this.vendorIdExists = false;
      this.checkingVendorId = false;
      clearTimeout(this.vendorIdCheckTimeout);
      this.contactEmailErrors = {};
      this.checkingContactEmails = {};
      Object.keys(this.contactEmailCheckTimeouts).forEach(key => {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      });
      this.contactEmailCheckTimeouts = {};
      this.documents = [];
      this.selectedDocumentIndex = null;
      this.documentPreviewUrls = {};
      this.currentPreviewUrl = '';
      this.loadingPreview = false;
      this.previewError = false;
    },
    cancel() {
      this.$router.go(-1);
    },
    fillFromSelectedAddress() {
      const addr = this.addresses.find(a => a.type === this.selectedAddressType);
      if (addr) {
        this.addressDetails = [
          addr.address1,
          addr.address2,
          addr.city,
          addr.state,
          addr.postalCode,
          addr.country
        ].filter(Boolean).join('\n');
      } else {
        this.addressDetails = '';
      }
    },
    showError(title, message) {
      this.errorTitle = title;
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    addValidationError(message, tab = null) {
      if (!this.validationErrors.includes(message)) {
        this.validationErrors.push(message);
        if (tab && !this.errorTabs.includes(tab)) {
          this.errorTabs.push(tab);
        }
      }
    },
    showAllErrors() {
      if (this.validationErrors.length > 0) {
        this.errorTitle = 'Validation Errors';
        this.errorMessage = this.validationErrors.join('\n\n');
        this.showErrorModal = true;
      }
    },
    clearValidationErrors() {
      this.validationErrors = [];
      this.errorTabs = [];
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorTitle = 'Validation Error';
      this.errorMessage = '';
      this.clearValidationErrors();
    },
    showSuccess(title, message, action = 'new') {
      this.successTitle = title;
      this.successMessage = message;
      this.successAction = action;
      this.showSuccessModal = true;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successTitle = 'Success';
      this.successMessage = '';
    },
    stayOnPageAfterSave() {
      this.closeSuccessModal();
      if (this.successAction === 'close' && this.createdRecordId) {
        this.$router.push(`/company/${this.$route.params.id}/dashboard/vendors/edit/${this.createdRecordId}`);
        return;
      }
      this.resetForm();
      this.activeTab = 'general';
    },
    leavePageAfterSave() {
      this.closeSuccessModal();
      this.$router.go(-1);
    },
    handleContactEmailInput(email, idx, isNew) {
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      this.clearContactEmailError(key);
      
      if (!email || !email.trim()) {
        return;
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // Debounce the validation
      this.contactEmailCheckTimeouts[key] = setTimeout(() => {
        this.validateContactEmail(email, idx, isNew);
      }, 500);
    },
    async validateContactEmail(email, idx, isNew, immediate = false) {
      if (!email || !email.trim()) {
        return;
      }
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
        this.contactEmailErrors[key] = 'Please enter a valid email address.';
        this.checkingContactEmails[key] = false;
        return;
      }
      
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      const companyId = this.$route.params.id;
      
      if (!companyId) {
        this.checkingContactEmails[key] = false;
        return;
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // If immediate, check right away; otherwise debounce
      const performCheck = async () => {
        this.checkingContactEmails[key] = true;
        delete this.contactEmailErrors[key];
        
        try {
          const response = await vendorService.checkVendorEmailExists(email.trim(), companyId);
          
          if (response.data.exists) {
            let errorMessage = '';
            if (response.data.existsAsConsultant) {
              errorMessage = 'This email address already exists for a consultant in this company.';
            } else if (response.data.existsAsContact) {
              errorMessage = 'This email address already exists for a customer contact in this company.';
            } else if (response.data.existsAsVendor) {
              errorMessage = 'This email address already exists for another vendor or vendor contact in this company.';
            } else {
              errorMessage = 'This email address is already in use.';
            }
            this.contactEmailErrors[key] = errorMessage;
          }
        } catch (error) {
          logger.error('Error checking contact email:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingContactEmails[key] = false;
        }
      };
      
      if (immediate) {
        await performCheck();
      } else {
        this.contactEmailCheckTimeouts[key] = setTimeout(performCheck, 500);
      }
    },
    clearContactEmailError(key) {
      delete this.contactEmailErrors[key];
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
        delete this.contactEmailCheckTimeouts[key];
      }
      this.checkingContactEmails[key] = false;
    },
    // Document handling methods
    handleDocumentFileUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);
      let processedCount = 0;
      const totalFiles = fileArray.length;

      fileArray.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const getMimeType = (filename, fallbackType) => {
            if (fallbackType && fallbackType !== '') return fallbackType;
            const extension = filename.split('.').pop().toLowerCase();
            const mimeMap = {
              'pdf': 'application/pdf',
              'doc': 'application/msword',
              'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'xls': 'application/vnd.ms-excel',
              'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'png': 'image/png',
              'gif': 'image/gif',
              'txt': 'text/plain'
            };
            return mimeMap[extension] || 'application/octet-stream';
          };

          const document = {
            fileName: file.name,
            fileSize: file.size,
            filePath: e.target.result,
            mimeType: getMimeType(file.name, file.type),
            uploadedAt: new Date().toISOString(),
            file: file // Store the actual file for upload
          };
          this.documents.push(document);
          processedCount++;
          
          // Auto-select the last document when all files are processed
          if (processedCount === totalFiles) {
            this.$nextTick(async () => {
              this.selectedDocumentIndex = this.documents.length - 1;
              await this.selectDocument(this.selectedDocumentIndex);
            });
          } else if (this.selectedDocumentIndex === null && processedCount === 1) {
            // Select first document if none selected
            this.$nextTick(async () => {
              this.selectedDocumentIndex = 0;
              await this.selectDocument(0);
            });
          }
        };
        reader.readAsDataURL(file);
      });

      if (this.$refs.addDocumentFileInput) {
        this.$refs.addDocumentFileInput.value = '';
      }
    },
    removeVendorDocument(index) {
      if (this.documents && this.documents.length > index) {
        // Remove preview URL from cache and revoke blob URL if needed
        const document = this.documents[index];
        if (document && document.filePath) {
          const cachedUrl = this.documentPreviewUrls[document.filePath];
          if (cachedUrl && cachedUrl.startsWith('blob:')) {
            window.URL.revokeObjectURL(cachedUrl);
          }
          delete this.documentPreviewUrls[document.filePath];
        }
        
        // If removing the selected document, clear selection and preview
        if (this.selectedDocumentIndex === index) {
          this.selectedDocumentIndex = null;
          this.currentPreviewUrl = '';
          this.previewError = false;
        } else if (this.selectedDocumentIndex > index) {
          // Adjust selected index if document before it was removed
          this.selectedDocumentIndex--;
        }
        
        this.documents.splice(index, 1);
      }
    },
    async selectDocument(index) {
      this.selectedDocumentIndex = index;
      this.previewError = false;
      this.currentPreviewUrl = '';
      
      const document = this.documents[index];
      if (!document) return;
      
      // Check if URL is already cached
      if (this.documentPreviewUrls[document.filePath]) {
        this.currentPreviewUrl = this.documentPreviewUrls[document.filePath];
        return;
      }
      
      // Load preview URL
      this.loadingPreview = true;
      try {
        const url = await this.getDocumentPreviewUrl(document);
        if (url) {
          this.currentPreviewUrl = url;
        } else {
          this.previewError = true;
        }
      } catch (error) {
        logger.error('Error loading preview:', error);
        this.previewError = true;
      } finally {
        this.loadingPreview = false;
      }
    },
    handlePreviewError() {
      this.previewError = true;
      this.currentPreviewUrl = '';
    },
    async getDocumentPreviewUrl(document) {
      if (!document || !document.filePath) return '';
      
      // If already cached, return cached URL
      if (this.documentPreviewUrls[document.filePath]) {
        return this.documentPreviewUrls[document.filePath];
      }
      
      // If it's a new document (data URL), use it directly
      if (document.filePath.startsWith('data:')) {
        this.documentPreviewUrls[document.filePath] = document.filePath;
        return document.filePath;
      }
      
      // For saved documents, fetch from backend
      if (document.id) {
        try {
          const companyId = this.$route.params.id;
          if (!companyId) return '';
          
          const response = await vendorService.viewVendorDocument(document.id, companyId);
          const blob = new Blob([response.data], { type: document.mimeType || 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          this.documentPreviewUrls[document.filePath] = url;
          return url;
        } catch (error) {
          logger.error('Failed to load document preview:', error);
          return '';
        }
      }
      
      return '';
    },
    isImageFile(document) {
      if (!document || !document.mimeType) return false;
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return imageTypes.includes(document.mimeType.toLowerCase());
    },
    isPdfFile(document) {
      if (!document || !document.mimeType) return false;
      return document.mimeType.toLowerCase() === 'application/pdf';
    },
    async openDocumentInNewWindow(document) {
      const url = await this.getDocumentPreviewUrl(document);
      if (url) {
        window.open(url, '_blank');
      } else {
        this.showError('Error', 'Failed to open document');
      }
    },
    async uploadDocuments(vendorId) {
      if (!this.documents || this.documents.length === 0) return;
      
      const companyId = this.$route.params.id;
      if (!companyId) {
        logger.error('Company ID not found');
        return;
      }
      
      // Filter documents that haven't been uploaded yet (have file property)
      const documentsToUpload = this.documents.filter(doc => doc.file);
      
      if (documentsToUpload.length === 0) return;
      
      try {
        const formData = new FormData();
        formData.append('vendorId', vendorId);
        formData.append('companyId', companyId);
        
        documentsToUpload.forEach((doc) => {
          if (doc.file) {
            formData.append('documents', doc.file);
          }
        });
        
        const response = await vendorService.uploadVendorDocuments(vendorId, companyId, formData);
        logger.log('Documents uploaded successfully', response.data);
        
        // Update documents array with uploaded document IDs
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const uploadedDocs = response.data.data;
          documentsToUpload.forEach((doc, index) => {
            if (uploadedDocs[index]) {
              const uploadedDoc = uploadedDocs[index];
              // Find the document in the array and update it
              const docIndex = this.documents.findIndex(d => d === doc);
              if (docIndex !== -1) {
                this.documents[docIndex].id = uploadedDoc.id;
                this.documents[docIndex].vendorId = uploadedDoc.vendorId;
                this.documents[docIndex].uploadedAt = uploadedDoc.uploadedAt;
                // Keep filePath as data URL for preview, but also store the filePath from server
                this.documents[docIndex].serverFilePath = uploadedDoc.filePath;
                
                // If this is the currently selected document, reload preview
                if (this.selectedDocumentIndex === docIndex) {
                  this.$nextTick(() => {
                    this.selectDocument(docIndex);
                  });
                }
              }
            }
          });
        }
      } catch (error) {
        logger.error('Error uploading documents:', error);
        // Don't throw - allow vendor creation to succeed even if document upload fails
        this.showError('Warning', 'Vendor created but some documents failed to upload: ' + (error.message || 'Please try uploading documents again'));
      }
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    }
  },
  beforeUnmount() {
    // Cleanup blob URLs to prevent memory leaks
    Object.values(this.documentPreviewUrls).forEach(url => {
      if (url && url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    });
  },
  watch: {
    openingBalance(val) {
      // Only set balance if creating a new vendor (not editing)
      this.balance = val;
    },
    contacts: {
      handler(newContacts) {
        const primary = newContacts.find(c => c.primary);
        if (primary) {
          this.phone = primary.phone || '';
          this.email = primary.email || '';
        } else {
          this.phone = '';
          this.email = '';
        }
      },
      deep: true
    },
    selectedVendorAddressDetails() {
      this.autoResizeAddressTextarea();
    },
    selectedAddressType() {
      this.fillFromSelectedAddress();
    },
  },
  async created() {
    try {
      const companyId = this.$route.params.id;
      const res = await ApiService.getCompanyAccounts(companyId);
      const allAccounts = res.data.accounts || res.data || [];
      const allowedTypes = [
        'Expense', 'Other Expense', 'Cost of Goods Sold', 'Cash', 'Bank', 'Credit Card', 'Current Liability', 'Paypal'
      ];
      this.accounts = allAccounts;
      this.filteredAccounts = allAccounts.filter(a => allowedTypes.includes(a.accountType));
      // Fetch payment terms
      await this.fetchPaymentTerms();
      // Fetch vendor groups
      await this.fetchVendorGroups();
      // Fetch shipping methods from vendor API
      await this.fetchShippingMethods();
    } catch (e) {
      this.accounts = [];
      this.filteredAccounts = [];
      this.paymentTermsList = [];
      this.vendorGroups = [];
      this.shippingMethodsList = [];
    }
  },
}
</script>

<style src="@/components/ManageCustomFields.css"></style> 